import {
  Stack,
  Typography,
  Grid,
  Box,
  GridProps
} from "@mui/material";
import {
  styled,
  useTheme
} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Bookmark, ChevronRight, FileBadge } from "lucide-react";
import {
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useOutletContext
} from "react-router-dom";
import { isAddFavouriteVisible } from "./assertions";
import type { CardCtaProps } from "./CardCta.types";
import {
  Icon,
  Link,
  LinkBehavior,
  Modal,
  Spinner
} from "../../components";
import { ButtonDigited, StyledTypography } from "../../components/Button/Button.variants";
import { LEVEL, STATUS_LO } from "../../consts";
import {
  shouldGoToWatchByLabel,
  useButtonStatusMap,
  usePlayResumeVideo
} from "../../hooks";
import {
  useFavouritesMutation,
  useInvalidateLearningObjectQuery,
  useRatingQuery
} from "../../queries";
import { OutletContext } from "../../routes";
import { useNavigate } from "../../services";
import { useAuthStore, useLanguageStore } from "../../stores";
import { getError } from "../../utils/error";
import { getLearningObjectType } from "../../utils/general";
import { Progress } from "../Progress/Progress.component";

const CardCtaGrid = styled(Grid)<
  GridProps & { $vertical?: boolean }
>(({ $vertical = false }) => ({
  alignItems: $vertical ? undefined : "center",
  flexDirection: $vertical ? "column" : undefined,
  position: "relative",
  transition: "gap .3s linear"
}));

export const StyledBoxContact = styled(Box)<{ $visible: boolean }>(({ theme, $visible }) => ({
  alignItems: "center",
  background: theme.customColors.backgroundSecondary,
  borderRadius: "2px",
  boxShadow: "8px 8px 15px rgba(0, 0, 0, 0.15)",
  color: theme.customColors.textInput,
  display: $visible ? "flex" : "none",
  gap: theme.spacing(3),
  justifyContent: "space-between",
  maxHeight: $visible ? "auto" : 0,
  maxWidth: "450px",
  opacity: $visible ? 1 : 0,
  padding: $visible ? theme.spacing(1, 2) : theme.spacing(0, 2),
  position: "absolute",
  top: 0,
  transition: "all .3s linear",
  width: "fit-content",
  zIndex: theme.zIndex.mobileStepper,

  ".icon": {
    cursor: "pointer",
    minWidth: "24px"
  },

  "> *": {
    maxHeight: $visible ? "auto" : 0
  }
}));

export const StyledCreditsTooltipIcon = styled(Box)(({ theme }) => ({
  marginLeft: 10,
  zIndex: `${theme.zIndex.mobileStepper + 1} !important`
}));

export const StyledSnackBarFavourite = styled(Box)<{ $visible: boolean, $cardType?: string }>(({
  theme,
  $visible,
  $cardType
}) => ({
  alignItems: "center",
  background: theme.customColors.backgroundPrimary,
  borderRadius: "2px",
  boxShadow: "8px 8px 15px rgba(0, 0, 0, 0.15)",
  color: theme.customColors.textInput,
  display: "flex",
  gap: theme.spacing(3),
  justifyContent: "space-between",
  maxHeight: $visible ? "auto" : 0,
  zIndex: $visible ? 9999 : 0,
  maxWidth: "450px",
  opacity: $visible ? 1 : 0,
  padding: $visible ? theme.spacing(1, 2) : theme.spacing(0, 2),
  pointerEvents: $visible ? "auto" : "none",
  position: "absolute",
  right: $cardType === "small" ? 0 : undefined,
  left: $cardType === "small" ? undefined : "3rem",
  top: $cardType === "small" ? "-3.15rem" : "0rem",
  transition: "all .3s linear",
  width: "fit-content",
  flexWrap: "nowrap",
  whiteSpace: "nowrap",
  ".icon": {
    cursor: "pointer",
    minWidth: "24px"
  },

  ">p, >a": {
    fontSize: "0.75rem"
  },

  "> *": {
    maxHeight: $visible ? "auto" : 0,
    opacity: $visible ? "auto" : 0
  }
}));


export function CardCta({
  badge,
  editionsLength = 0,
  courseId = null,
  disabled = false,
  ecmRegistration,
  enrollType,
  id,
  isBookmarkPage = false,
  isCard = false,
  isSurvey,
  isTest,
  isEditorial,
  madeAttempts,
  ecm_specialization,
  isExpired,
  isStandAlone,
  labelMoreInfo = false,
  learningObjectType,
  learningObjectTypology,
  linkVirtualClass,
  percentageOfCompletion,
  parentId, // for favourites
  grandParentId, // for favourites
  rootId = null,
  setOpenRegistrationModal,
  setOpenModalEcm,
  setOpenTojModal,
  status,
  simple = false,
  triggerOpenTabs,
  vertical = false,
  canAccessNow,
  showDetailsButton = false,
  ctaPosition = "start",
  isLinkRelative = false,
  isExplorePage,
  isHeroBanner = false,
  link,
  cardType,
  attributesIds=[],
  isHovered,
  isFavourite,
  externalFileUrl,
  customBorder,
  categories: _categories,
  contents: _contents,
  attributes: _attributes,
  certificates,
  ...props
}: CardCtaProps) {
  const { sectionUrl } = useOutletContext<OutletContext>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = window.location.href.includes("per-te") ? "per-te" : "esplora";

  const {
    resumeLink,
    sendRequest,
    bannerVisible,
    enrollmentMutationHadSuccess,
    enrollmentIsLoading,
    enrollmentMutationError,
    resumeLinkOpenEsternal,
    enrollmentMutationIsError,
    setBannerVisible,
    actionButton,
    resumeContent
  } = usePlayResumeVideo({
    ecm_specialization,
    ecmRegistration,
    enrollType,
    isCard,
    learningObjectType,
    learningObjectTypology,
    id,
    labelMoreInfo,
    sectionUrl: currentPath,
    status,
    setOpenTojModal,
    setOpenRegistrationModal,
    triggerOpenTabs,
    linkVirtualClass,
    navigate,
    parentId,
    rootId,
    grandParentId,
    courseId,
    isExpired,
    attributesIds
  });

  const buttonText = useButtonStatusMap({
    ecm_specialization: ecm_specialization,
    ecmRegistration,
    enrollType,
    isCard,
    isSurvey: isSurvey,
    isTest: isTest,
    learningObjectType,
    learningObjectTypology,
    madeAttempts: madeAttempts,
    percentageOfCompletion
  });

  const theme = useTheme();
  // const isLoading = useAuthStore(state => state.isLoading);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const language = useLanguageStore(state => state.language)?.id;
  const userData = useAuthStore(state => state.userData);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const [showModal, setShowModal] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [ariaMessage, setAriaMessage] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Determina se ci troviamo nella pagina bookmarks
  const isBookmarksPage = location.pathname.includes("/bookmarks");

  // Prepara le query da invalidare solo se siamo nella pagina bookmarks
  const getInvalidateContentQueries = () => {
    if (!isBookmarksPage || !userData) return [];

    // Ritorna un array con un semplice marker per indicare che dobbiamo invalidare le query dei preferiti
    return [{
      apiPath: "", // Non serve un path specifico, useremo pattern matching
      isSeedingSession,
      language,
      additionalKeys: []
    }];
  };

  const hasEnabledBadge = !!badge?.isEnable;
  const hasEnabledCertificate = !!certificates?.some(c => c?.isEnable);

  const { invalidateLearningObject } = useInvalidateLearningObjectQuery({
    courseId: courseId,
    id: id,
    learningObjectTypology: learningObjectTypology,
    rootId: rootId
  });

  const { data: ratingStatistic, isLoading: ratingIsLoading } = useRatingQuery({
    enabled: isHovered ?? true,
    learningObjectId: id,
    learningObjectTypology: getLearningObjectType(learningObjectTypology)
  });

  // TO ADD IN PARAMS IN FAVOURITES RETRIEVED FROM LO
  const favParentId = !isStandAlone ? (!courseId && rootId) ? rootId : courseId ?? null : null;

  const favGrandParentId = (!isStandAlone
    && favParentId
    && favParentId !== rootId
  ) ? rootId : null;


  const editorialParentId = (!courseId && rootId) ? rootId : courseId ?? null;
  const editorialGrandParentId = (editorialParentId && editorialParentId !== rootId) ? rootId : null;

  const {
    mutate: addFavouriteMutation,
    isSuccess: isFavouriteAdded,
    isPending: isAddingFavouriteLoading
  } = useFavouritesMutation({
    grandParentId: favGrandParentId,
    learningObjectId: id,
    learningObjectTypology,
    method: "POST",
    parentId: favParentId,
    invalidateContentQueries: getInvalidateContentQueries()
  });

  const {
    mutate: removeFavouriteMutation,
    isSuccess: isFavouriteRemoved,
    isPending: isRemovingFavouriteLoading
  } = useFavouritesMutation({
    invalidateContentQueries: getInvalidateContentQueries(),
    invalidateDelay: isBookmarksPage ? 2000 : 0, // 2 secondi di delay solo nella pagina bookmarks
    learningObjectId: id,
    learningObjectTypology,
    method: "DELETE"
  });

  const loStatistic = {
    userFavourite: ratingStatistic
      ? Boolean(ratingStatistic?.userRating.isFavouriteForLearner && ratingStatistic?.objectStatistics?.learningObjectId)
      : Boolean(isFavourite)
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Handle added favourite
    if (isFavouriteAdded && loStatistic.userFavourite) {
      setSnackbarVisible(true);
      setAriaMessage(t("has_been_added"));
      timer = setTimeout(() => {
        setSnackbarVisible(false);
      }, 5000);
    }

    // Handle removed favourite
    else if (isFavouriteRemoved && !loStatistic.userFavourite) {
      setSnackbarVisible(true);
      setAriaMessage(t("has_been_removed"));
      timer = setTimeout(() => {
        setSnackbarVisible(false);
      }, 5000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    isFavouriteAdded,
    isFavouriteRemoved,
    loStatistic.userFavourite
  ]);


  useEffect(() => {
    if (enrollmentMutationHadSuccess && !enrollmentIsLoading) {
      if (enrollType === "AUTO_ENROLL") {
        if (resumeLinkOpenEsternal) {
          window.open(resumeLinkOpenEsternal, "_blank", "noreferrer");
        } else {
          resumeContent();
        }
      } else {
        invalidateLearningObject();
      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrollType,
    enrollmentIsLoading,
    enrollmentMutationHadSuccess,
    navigate,
    resumeLink,
    courseId,
    rootId,
    resumeLinkOpenEsternal
  ]);

  const viewDetails = () => {
    navigate.navigateToDetail({
      id,
      isForYou: currentPath === "per-te",
      learningObjectTypology,
      courseId: courseId ?? parentId,
      rootId: rootId ?? grandParentId
    });
  };

  function handleRedirectUrl() {
    if (isLinkRelative && externalFileUrl) {
      let cleanedLink = externalFileUrl;

      if (!cleanedLink.startsWith("/")) cleanedLink = "/" + cleanedLink;
      const regex = /\/$/; //check if url has / at the end
      const base = regex.test(externalFileUrl)
        ? `${cleanedLink}${id}` //link url and id without / in between
        : `${cleanedLink}/${id}`; //link url and id with / in between

      const queryParams = new URLSearchParams();
      if (editorialParentId) queryParams.set("parentId", editorialParentId.toString());
      if (editorialGrandParentId) queryParams.set("grandParentId", editorialGrandParentId.toString());
      const queryString = queryParams.toString();
      return queryString ? `${base}?${queryString}` : base;
    }
    return `${externalFileUrl}`;
  }

  useEffect(() => {
    enrollmentMutationIsError && setShowModal(true);
  }, [enrollmentMutationIsError]);

  const primaryCtaClick = () => {
    if (disabled) return;

    if (learningObjectTypology === "DA_URL_RES" && isLinkRelative && link) {
      let cleanedLink = link;

      if(!cleanedLink.startsWith("/")){
        cleanedLink = "/" + cleanedLink;
      }

      if(cleanedLink.endsWith("/")){
        cleanedLink = cleanedLink.slice(0, -1);
      }

      let url = `${cleanedLink}/${id}`;
      const queryParams = new URLSearchParams();
      if (editorialParentId) queryParams.set("parentId", editorialParentId.toString());
      if (editorialGrandParentId) queryParams.set("grandParentId", editorialGrandParentId.toString());
      const queryString = queryParams.toString();
      if (queryString) url = `${url}?${queryString}`;
      navigate(url);
    } else {
      if (actionButton.function){
        return actionButton.function();
      }else if(!isLinkRelative){
        window.open(resumeLink, "_blank", "noopener,noreferrer");
      }else{
        navigate(resumeLink);
      }
    }
  };

  const showProgressBar = !!(!simple && !isCard && !isHeroBanner && percentageOfCompletion && (status === STATUS_LO.P || status === STATUS_LO.C) && (learningObjectTypology === LEVEL.PATH || learningObjectTypology === LEVEL.COURSE));

  const showPrimaryButton =()=> {
    if(sectionUrl === "per-te" && !isHeroBanner) return false; // no primary button in per-te section

    if(getLinkLabel() === t("editions_show") &&  editionsLength === 0) return false; // primary button for editions must be hide if there are no editions

    return true;
  };

  function buttonTypeHandler() {
    if(getLinkLabel() === t("details")){ //TODO condizione da rifinire se necessario
      return "secondary";
    }
    return "primary";
  }

  const isLoading = enrollmentIsLoading && (!enrollmentMutationHadSuccess || enrollmentMutationError);

  const primaryButtonHandler = () => {
    if(!isEditorial){
      if(!showDetailsButton && getLinkLabel() === t("details")) return;

      return (
        <Box
          position={ "relative" }
          sx={ { pointerEvents:"all" } }
        >
          { isLoading && (
            <Spinner
              size={ "1.5rem" }
              padding={ 0 }
              progressColor={
                getLinkLabel() !== t("details") ? theme.customColors.textTag : theme.customColors.accentVariantA
              }
              sxBox={ {
                position: "absolute",
                top:"50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 99
              } }
            />
          ) }
          <ButtonDigited
            id={ `card-cta-${id}-primary-button` }
            data-testid="card-cta-primary-button"
            typology={ buttonTypeHandler() }
            buttonSize="md"
            aria-label={ getLinkLabel() }
            onClick={ (e) => {
              if(isLoading) return;
              e.stopPropagation();
              primaryCtaClick();
            } }
            disabled={ disabled || !canAccessNow }
            text={
              <Typography
                sx={ {
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  lineHeight: "1.5rem",
                  opacity: isLoading ? 0 : 1
                } }
              >
                { getLinkLabel() }
              </Typography>
            }
            sxTooltip={ !canAccessNow ? {
              border: `1px solid ${theme.customColors.borderWarning}`,
              background: theme.customColors.backgroundWarning,
              borderRadius: "0.2rem",
              margin: isMobile ? "20px 20px !important" : "20px 40px !important",
              ".MuiTooltip-tooltip":{
                padding:"0.4rem !important",
                margin:"0 !important",
                color: theme.customColors.textTag,
                background: theme.customColors.backgroundWarning
              }
            } : null }
            boxTooltipProps={ {
              alignItems: "center"
            } }
            tooltipMessage={ !canAccessNow ? t("learning_object_access_badge_message") : undefined }
          />
        </Box>
      );
    }
    if(isEditorial){
      return <Link
        id={ `card-cta-${id}-editorial-link` }
        data-testid="card-cta-editorial-link"
        role="button"
        tabIndex={ 0 }
        href={ handleRedirectUrl() }
        { ...(isLinkRelative
          ? { component: LinkBehavior } // link interno: navigazione SPA react-router (mantiene lo stato Zustand)
          : { rel: "noopener noreferrer", target: "_blank" }) }
        aria-label={ t("discover_more") }
        sx={ { "&:focus-visible": {
          outline: `2px solid ${theme.customColors.headerIcon}`,
          outlineOffset: "2px"
        } } }
      >
        <ButtonDigited
          data-testid="card-cta-editorial-button"
          tabIndex={ -1 }
          typology="primary"
          buttonSize="md"
          disabled={ disabled || !canAccessNow }
        >
          <StyledTypography sx={ { color: theme.customColors.textPrimaryCta, display:"flex", alignItems:"center", gap:"0.35rem" } }>{ t("discover_more") + " " }<ChevronRight size="0.875rem" /></StyledTypography>
        </ButtonDigited>
      </Link>;
    }
    return <></>;
  };


  return (
    <CardCtaGrid
      id={ `card-cta-${id}` }
      data-testid="card-cta-root"
      item
      container
      gap={ bannerVisible ? 1 : 0 }
      $vertical={ vertical }
      overflow={ snackbarVisible || bannerVisible ? "visible !important" : "hidden !important" }
      { ...props }
    >
      { bannerVisible  &&
        <StyledBoxContact
          $visible={ bannerVisible }
          width="100%"
          sx={ {
            left: 0,
            right: "unset",
            top: "-40px"
          } }
        >
          <Typography
            color={ theme.customColors.textPrimaryAlternative }
            fontSize={ theme.spacing(1.625) }
            fontWeight={ 500 }
          >
            { bannerVisible ? t("contact_text") : "" }
          </Typography>
          <Icon
            aria-hidden="true"
            icon="close"
            size={ 24 }
            color={ theme.palette.primary?.main }
            onClick={ (e) => {
              e.stopPropagation();
              setBannerVisible(false);
            } }
          />
        </StyledBoxContact>
      }
      <Stack
        direction={ "row" }
        gap={ 1 }
        width={ !isHeroBanner ? "100%" : "fit-content" }
        { ...props }
        sx={ {
          pointerEvents: "all",
          ">button, >a": {
            pointerEvents: setOpenModalEcm && disabled ? "none" : "auto",
            zIndex: setOpenModalEcm && disabled ? "-1" : undefined
          },
          justifyContent: `${ctaPosition === "split" ? "justify-between" : "start"} !important`
        } }
        onClick={ (e) => {
          e.stopPropagation();
          if (setOpenModalEcm && disabled && ecm_specialization && !ecmRegistration) {
            setOpenModalEcm(true);
          }
        } }
      >
        { /* Questa validazine serve ad evitare la presenza del gap in caso in cui il componente stack è vuoto */ }
        { (!(!showProgressBar && !(showDetailsButton && shouldGoToWatchByLabel(buttonText[status]?.label) && !isEditorial))) || (location.pathname !== "/per-te") ?
          <Stack
            direction={ "row" }
            flexWrap={ "wrap" }
            sx={ {
              justifyContent: "start !important",
              width: showProgressBar ? "100%" : "auto"
            } }
            gap="0.5rem"
          >
            { /*:TODO emilinare? ma cos'è questa progress??  */ }
            {
              showProgressBar
                ? (
                  <div style={ {
                    flexGrow: 1
                  } }>
                    <Progress
                      color={ props.color || theme.customColors.textPrimary }
                      flexGrow={ 1 }
                      width={ isMobile ? "100%" : "auto" }
                      maxWidth={ "none" }
                      percentageOfCompletion={ percentageOfCompletion }
                    />
                  </div>
                ) : null
            }
            { /*Nella sezione per-te viene nascosto il primary button, e viene sostituito dal primary button presente nella HomeCard , così da
                utilizzare la logica identica al pre-retyle */ }
            { showPrimaryButton() ? primaryButtonHandler() : null }
            { (showDetailsButton && shouldGoToWatchByLabel(buttonText[status]?.label) && (!isEditorial)) ? (
              <ButtonDigited
                id={ `card-cta-${id}-details-button` }
                data-testid="card-cta-details-button"
                buttonSize="md"
                typology="secondary"
                onClick={ (e) => {
                  e.stopPropagation();
                  viewDetails();
                } }
                aria-label={ t("details") }
                disabled={ disabled }
                text={ t("details") }
              />
            ) : null }
          </Stack>
          : null }
        <Stack
          direction={ "row" }
          alignItems={ "center" }
          sx={ {
            flex: (ctaPosition === "split" ? 1 : 0),
            justifyContent: `${(ctaPosition === "split") ? "end" : "start"} !important`,
            width: "auto"
          } }
          gap={ 1 }
        >
          { isAddFavouriteVisible(isHeroBanner, smartConfiguration, rootId, courseId, learningObjectTypology, simple, isBookmarkPage, isEditorial, isExplorePage, isCard) ?
            <Stack
              position={ "relative" }
            >
              { /* SNACKBAR FAVOURITE */ }
              <StyledSnackBarFavourite
                $visible={ snackbarVisible }
                $cardType={ cardType }
                id="snackBarFavourite"
                onClick={ ()=> null }
              >
                <Typography
                  aria-hidden="true"
                  color={ theme.customColors.textPrimary }
                  fontSize={ theme.spacing(1.625) }
                  fontWeight={ 500 }
                >
                  { loStatistic.userFavourite ? t("item_added_favourites") : t("item_removed_favourites") }
                  <Link
                    aria-hidden="true"
                    tabIndex={ -1 }
                    onClick={ (e) => {
                      e.stopPropagation();
                    } }
                    href="/bookmarks" fontWeight={ 700 } />
                </Typography>
              </StyledSnackBarFavourite>
              <ButtonDigited
                id={ `card-cta-${id}-favourite-toggle` }
                data-testid="card-cta-favourite-toggle"
                aria-label={ t(loStatistic.userFavourite ? "remove_from_favourites" : "add_to_favourites") }
                aria-pressed={ loStatistic.userFavourite }
                title={ t(loStatistic.userFavourite ? "remove_from_favourites" : "add_to_favourites") }
                disabled={ isHeroBanner ? (isAddingFavouriteLoading || isRemovingFavouriteLoading) : disabled }
                className="bookmark"
                typology="icononly"
                buttonSize="md"
                onMouseDown={ (e) => e.preventDefault() }
                onClick={ (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (loStatistic.userFavourite) {

                    removeFavouriteMutation();
                  } else {
                    addFavouriteMutation();
                  }
                } }

              >
                <>
                  { /* ICON FAVOURITE */ }
                  {
                    (isAddingFavouriteLoading || isRemovingFavouriteLoading || (isHovered && ratingIsLoading)) ?
                      <Spinner />
                      : (
                        <Bookmark
                          fill={ loStatistic.userFavourite ? theme.customColors.textPrimary : "transparent" }
                          stroke={ theme.customColors.textPrimary }
                          aria-hidden={ !loStatistic.userFavourite ? "true" : undefined }
                          aria-label={ snackbarVisible ? "" : t("remove_from_favourites") }
                        />
                      )
                  }
                  <Box
                    aria-live="polite"
                    sx={ {
                      position: "absolute",
                      width: 1,
                      height: 1,
                      overflow: "hidden",
                      clip: "rect(0 0 0 0)",
                      whiteSpace: "nowrap"
                    } }
                  >
                    { ariaMessage }
                  </Box>
                </>
              </ButtonDigited>
            </Stack>
            : null
          }
          { (sectionUrl === "esplora") &&  (badge || (certificates?.length ?? 0) > 0) ?
            <ButtonDigited
              id={ `card-cta-${id}-badge-button` }
              data-testid="card-cta-badge-button"
              aria-label={ t("download") }
              buttonSize="md"
              typology="icononly"
              disabled={ !hasEnabledBadge && !hasEnabledCertificate }
              onClick={ (e)=> {
                if(hasEnabledBadge || hasEnabledCertificate){
                  e.preventDefault();
                  navigate("/profile");
                } else {
                  e.preventDefault();
                }
              } }
              sx={ {
                cursor:(hasEnabledBadge || hasEnabledCertificate) ? "pointer" :"not-allowed"
              } }
            >
              <FileBadge size={ 16 } color={ theme.customColors.textPrimary } />
            </ButtonDigited>
            : null
          }

        </Stack>
      </Stack>
      <Modal
        open={ showModal && enrollmentMutationIsError }
        onClose={ () => {
          setShowModal(false);
        } }
        description={ getError(enrollmentMutationError)?.[0]?.message ?? "" }
        title={ getError(enrollmentMutationError)?.[0]?.name ?? "" }
      />
    </CardCtaGrid>
  );

  function getLinkLabel() {
    if (isEditorial) {
      return t("more_info");
    }
    if (sendRequest) {
      return t("validation_text");
    }
    if (labelMoreInfo) {
      return t("more_info");
    }

    return t(buttonText[status]?.label);
  }
}
